import PQueue from "p-queue";
import { useEffect, useRef, useState } from "react";
import { uploadChunkToS3 } from "~/lib/s3";

const MAX_CONCURRENT_CHUNKS = 6;
const CHUNK_SIZE = 6 * 1024 * 1024;

interface MultipartUploadStarted {
  uploadId: string;
  key: string;
}

interface MultipartPartUrl {
  signedUrl: string;
}

interface MultipartPart {
  ETag: string;
  PartNumber: number;
}

interface UseMultipartFileUploadParams<TResult> {
  startUpload: (
    uploadId: string,
    file: File
  ) => Promise<MultipartUploadStarted>;
  getPartUrl: (
    uploadId: string,
    key: string,
    s3UploadId: string,
    partNumber: number
  ) => Promise<MultipartPartUrl>;
  completeUpload: (
    uploadId: string,
    key: string,
    s3UploadId: string,
    filename: string,
    contentType: string,
    sizeBytes: number,
    parts: MultipartPart[]
  ) => Promise<TResult>;
  onUploadComplete?: (result: TResult) => Promise<void>;
}

export const useMultipartFileUpload = <TResult>({
  startUpload,
  getPartUrl,
  completeUpload,
  onUploadComplete,
}: UseMultipartFileUploadParams<TResult>) => {
  const [uploadQueue] = useState(
    () => new PQueue({ concurrency: MAX_CONCURRENT_CHUNKS })
  );
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const resetProgressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(
    () => () => {
      if (resetProgressTimeoutRef.current) {
        clearTimeout(resetProgressTimeoutRef.current);
      }
    },
    []
  );

  const uploadFile = async (file: File): Promise<TResult> => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const uploadId = crypto.randomUUID();
      setUploadProgress(10);

      const { uploadId: s3UploadId, key } = await startUpload(uploadId, file);
      setUploadProgress(20);

      const chunkCount = Math.ceil(file.size / CHUNK_SIZE);
      const uploadedParts: MultipartPart[] = [];

      const uploadChunk = async (index: number) => {
        const partNumber = index + 1;
        const start = index * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const partUrlData = await getPartUrl(
          uploadId,
          key,
          s3UploadId,
          partNumber
        );
        const etagHeader = await uploadChunkToS3(
          partUrlData.signedUrl,
          chunk,
          file.type,
          undefined
        );

        if (!etagHeader) {
          throw new Error("Missing ETag in S3 response");
        }

        uploadedParts.push({
          ETag: etagHeader.replace(/"/g, ""),
          PartNumber: partNumber,
        });
        setUploadProgress(20 + Math.round((partNumber / chunkCount) * 60));
      };

      const chunkPromises: Promise<void>[] = [];
      for (let index = 0; index < chunkCount; index += 1) {
        chunkPromises.push(
          uploadQueue.add(() => uploadChunk(index), { priority: 0 })
        );
      }
      await Promise.all(chunkPromises);

      setUploadProgress(95);
      const result = await completeUpload(
        uploadId,
        key,
        s3UploadId,
        file.name,
        file.type,
        file.size,
        uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber)
      );
      await onUploadComplete?.(result);
      setUploadProgress(100);
      return result;
    } finally {
      setIsUploading(false);
      if (resetProgressTimeoutRef.current) {
        clearTimeout(resetProgressTimeoutRef.current);
      }
      resetProgressTimeoutRef.current = setTimeout(
        () => setUploadProgress(0),
        1000
      );
    }
  };

  return { uploadFile, isUploading, uploadProgress };
};

export type { MultipartPart };
